import { useState, useEffect } from 'react'
import { Download, X, Share } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed'
const DISMISS_DURATION_DAYS = 7

// Detect iOS
const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(userAgent)
}

// Detect if running as standalone (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showIOSInstructions, setShowIOSInstructions] = useState(false)

  useEffect(() => {
    console.log('[PWA] Initializing install prompt...')
    console.log('[PWA] User Agent:', navigator.userAgent)
    console.log('[PWA] Is iOS:', isIOS())
    console.log('[PWA] Is standalone:', isStandalone())

    // Check if already installed
    if (isStandalone()) {
      console.log('[PWA] Already installed as standalone app')
      setIsInstalled(true)
      return
    }

    // Check if user dismissed recently
    const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY)
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt)
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
      console.log('[PWA] Days since dismissed:', daysSinceDismissed)
      if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
        console.log('[PWA] Still in dismiss period, hiding prompt')
        return
      }
      localStorage.removeItem(INSTALL_DISMISSED_KEY)
    }

    // For iOS, show manual instructions after a delay
    if (isIOS()) {
      console.log('[PWA] iOS detected - will show manual instructions')
      const timer = setTimeout(() => {
        setShowIOSInstructions(true)
        setIsVisible(true)
      }, 3000) // Show after 3 seconds on iOS
      return () => clearTimeout(timer)
    }

    // For other browsers, listen for beforeinstallprompt
    console.log('[PWA] Adding beforeinstallprompt listener...')

    const handleBeforeInstall = (e: Event) => {
      console.log('[PWA] beforeinstallprompt event received!')
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsVisible(true)
    }

    const handleAppInstalled = () => {
      console.log('[PWA] App installed!')
      setIsInstalled(true)
      setIsVisible(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Fallback: If event doesn't fire after 5 seconds, check if we can show anyway
    // This helps with debugging
    const fallbackTimer = setTimeout(() => {
      console.log('[PWA] Fallback check - beforeinstallprompt not received')
      console.log('[PWA] To debug, open DevTools > Application > Manifest')
    }, 5000)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleAppInstalled)
      clearTimeout(fallbackTimer)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    console.log('[PWA] Triggering install prompt...')
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    console.log('[PWA] User choice:', outcome)

    if (outcome === 'accepted') {
      setIsVisible(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem(INSTALL_DISMISSED_KEY, new Date().toISOString())
  }

  if (isInstalled || !isVisible) return null

  // iOS specific UI with manual instructions
  if (showIOSInstructions) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-in sm:left-auto sm:right-4 sm:max-w-sm">
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Instalar aplicación
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Para instalar en iOS:
              </p>
              <ol className="text-xs text-gray-600 dark:text-gray-300 mt-2 space-y-1">
                <li className="flex items-center gap-1">
                  1. Toca el botón <Share className="w-3 h-3 inline" /> Compartir
                </li>
                <li>2. Selecciona "Añadir a pantalla de inicio"</li>
                <li>3. Toca "Añadir"</li>
              </ol>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Standard install prompt for Android/Desktop
  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-in sm:left-auto sm:right-4 sm:max-w-sm">
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-4 flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
          <Download className="w-5 h-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Instalar aplicación
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Accede más rápido desde tu dispositivo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Instalar
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
